import { MessageBarType, SearchBox, Spinner, FontIcon, IconButton } from '@fluentui/react';
import { Button } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';

import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { MessageComponent } from '../../../commons/component/MessageComponent';

// functions
import * as ComponentFunctions from './ProtocolsList.Function';

////i18Next
import { useTranslation } from 'react-i18next';
import { FaFileExcel } from 'react-icons/fa';

// Styles
import './ProtocolsList.Component.css';
import { ContractAssociationFormComponent } from '../../Contracts/Protocols/ContractAssociationForm/ContractAssociationFormComponent';
import { PractitionerDetailsMini } from '../../../commons/component/Practitioner/PractitionerDetails/PractitionerDetails.Mini';
import { ContractAssociationForm } from '../../Contracts/ContractAssociationForm/ContractAssociationForm.Component';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import ProtocolsMenuComponent from '../Protocols.Menu.tsx/Protocols.Menu.Component';
import ProtocolExportDerivedPatientsFormComponent from '../ProtocolExportDerivedPatients/Protocol.ExportDerivedPatients.Form.Component';

const ProtocolsListComponent = (props: ComponentFunctions.Props) => {

  const [selectedProtocol, setSelectedProtocol] = useState<Protocol | undefined>(undefined);

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);

  const [isExportDrawerOpen, { setTrue: openExportDrawer, setFalse: closeExportDrawer }] =
    useBoolean(false);

  const [searchText, setSearchText] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<number, boolean>>({});

  const navigate = useNavigate();
  const [t] = useTranslation();

  useEffect(() => {
    props.GetAllMedicalProtocols();
    props.GetAllPractitioners(0, 1000, undefined);
  }, []);

  const onViewDetailsEvent = (protocol: Protocol) => {
    navigate(`/protocolsagreements/${protocol.id}`);
  };

  const onAssociateContract = (protocol: Protocol) => {
    setSelectedProtocol(protocol);
    openPanel();
  };

  const onSearch = (newValue: string) => {
    setSearchText(newValue);
  };

  const HandleContractAssociation = (contract: Contract) => {
    console.log(contract.id);
  };

  const filteredProtocols = props.MedicalProtocols?.filter((item: Protocol) =>
    item.title.toLowerCase().includes(searchText.toLowerCase()) ||
    item.fullTitle?.toLowerCase().includes(searchText.toLowerCase())
  ) ?? [];

  const isOngoingStatus = (status: Protocol['status']) =>
    status?.code?.toLowerCase().includes('ongoing') ||
    status?.title?.toLowerCase().includes('ongoing') ||
    status?.title?.toLowerCase().includes('en curso');

  const groupedProtocols = (() => {
    const groupMap = new Map<number, { statusId: number; statusTitle: string; items: Protocol[] }>();
    filteredProtocols.forEach((item) => {
      if (!groupMap.has(item.statusId)) {
        groupMap.set(item.statusId, { statusId: item.statusId, statusTitle: item.status?.title ?? '', items: [] });
      }
      groupMap.get(item.statusId)!.items.push(item);
    });
    return Array.from(groupMap.values()).sort((a, b) => {
      const aOngoing = isOngoingStatus(a.items[0]?.status);
      const bOngoing = isOngoingStatus(b.items[0]?.status);
      if (aOngoing && !bOngoing) return -1;
      if (!aOngoing && bOngoing) return 1;
      return a.statusId - b.statusId;
    });
  })();

  const toggleGroup = (statusId: number) => {
    setCollapsedGroups((prev) => ({ ...prev, [statusId]: !prev[statusId] }));
  };

  //#region Subcontent definition

  const PrincipalErrorMessage = props.failOnLoading && (
    <MessageComponent
      message={t('protocolList.errorMessage')}
      subMessage={props.error?.ErrorMessage || ''}
      type={MessageBarType.error}
      itemList={props.error?.Errors || []}
    />
  );

  const Loading = props.isLoading && (
    <div className="protocols-list__loading">
      <Spinner
        label={t('protocolList.loadingProtocols')}
        ariaLive="assertive"
        labelPosition="top"
      />
    </div>
  );

  const renderCard = (item: Protocol) => (
    <div key={item.id} className="protocol-card-wrapper">
      <div
        className="protocol-card"
        onClick={() => onViewDetailsEvent(item)}
      >
        <div className="protocol-card__accent" />
        <div className="protocol-card__body">

          <div className="protocol-card__header">
            <div className="protocol-card__id-group">
              {item.protocolNumber && (
                <span className="protocol-card__number">#{item.protocolNumber}</span>
              )}
            </div>
            <IconButton
              iconProps={ComponentFunctions.LinkContractIcon}
              title={t('protocolList.button.protocolInformation')}
              ariaLabel="viewProtocol"
              className="protocol-card__action-btn"
              onClick={(e) => { e.stopPropagation(); onViewDetailsEvent(item); }}
            />
          </div>

          <div className="protocol-card__title-block">
            <h3 className="protocol-card__title">{item.title.toUpperCase()}</h3>
            {item.fullTitle && item.fullTitle !== item.title && (
              <p className="protocol-card__full-title">{item.fullTitle}</p>
            )}
          </div>

          <div className="protocol-card__tags">
            {item.phase?.title && (
              <span className="protocol-card__tag protocol-card__tag--phase">
                <FontIcon iconName="TestBeaker" />
                {item.phase.title}
              </span>
            )}
            {item.specialty?.description && (
              <span className="protocol-card__tag protocol-card__tag--specialty">
                <FontIcon iconName="Medical" />
                {item.specialty.description}
              </span>
            )}
            {item.laboratory?.title && (
              <span className="protocol-card__tag protocol-card__tag--lab">
                <FontIcon iconName="BuildDefinition" />
                {item.laboratory.title}
              </span>
            )}
          </div>

          <div className="protocol-card__footer">
            <div className="protocol-card__investigator">
              <PractitionerDetailsMini practitioner={props.GetPractiontioner(item.principalInvestigatorId)} />
            </div>
            {item.patientsObjective > 0 && (
              <div className="protocol-card__patients">
                <FontIcon iconName="People" />
                <span>{item.patientsObjective}</span>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );

  const ProtocolGroups = props.isLoadingSuccessfully && (
    filteredProtocols.length > 0
      ? (
        <div className="protocols-groups">
          {groupedProtocols.map((group) => {
            const collapsed = !!collapsedGroups[group.statusId];
            return (
              <div key={group.statusId} className={`protocols-group${collapsed ? ' protocols-group--collapsed' : ''}`}>
                <button
                  className="protocols-group__header"
                  onClick={() => toggleGroup(group.statusId)}
                >
                  <div className="protocols-group__header-left">
                    <FontIcon
                      iconName="ChevronDown"
                      className="protocols-group__chevron"
                    />
                    <span className={`protocols-group__status-dot protocols-group__status-dot--${group.statusId}`} />
                    <span className="protocols-group__title">{group.statusTitle}</span>
                    <span className="protocols-group__count">{group.items.length}</span>
                  </div>
                </button>
                <div className="protocols-group__body">
                  <div className="protocols-list__grid">
                    {group.items.map(renderCard)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )
      : (
        <div className="protocols-list__empty">
          <FontIcon iconName="SearchIssue" className="protocols-list__empty-icon" />
          <p>{t('protocolList.parameters.noData')}</p>
        </div>
      )
  );

  //#endregion

  return (
    <>
      <div className="protocols-page-header">
        <div className="protocols-page-header__title-group">
          <FontIcon iconName="TestBeakerSolid" className="protocols-page-header__icon" />
          <div>
            <h2 className="protocols-page-header__title">Protocolos</h2>
            {props.isLoadingSuccessfully && (
              <span className="protocols-page-header__count">
                {filteredProtocols.length} de {props.MedicalProtocols.length}
              </span>
            )}
          </div>
        </div>
        <div className="protocols-page-header__actions">
          <SearchBox
            placeholder={t('protocolList.search')}
            underlined={false}
            onChange={(
              event?: React.ChangeEvent<HTMLInputElement> | undefined,
              newValue?: string | undefined
            ) => (newValue ? onSearch(newValue) : undefined)}
            onClear={() => onSearch('')}
            className="protocols-page-header__search"
          />
          <Button
            appearance="outline"
            icon={<FaFileExcel />}
            title={t('protocolList.button.exportDerivedPatients')}
            onClick={openExportDrawer}
          >
            {t('protocolList.button.exportDerivedPatients')}
          </Button>
          <ProtocolsMenuComponent />
        </div>
      </div>

      {PrincipalErrorMessage}
      {ProtocolGroups}
      {Loading}

      <ContractAssociationForm
        isOpen={isOpen}
        openPanel={openPanel}
        dismissPanel={dismissPanel}
        ContractId={0}
        AssociateContract={HandleContractAssociation}
      />
      <ProtocolExportDerivedPatientsFormComponent
        isOpen={isExportDrawerOpen}
        onClose={closeExportDrawer}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ProtocolsListComponent as any);
